import { useMemo, useState } from "react";
import { ArrowRight, Plane, Briefcase, ShoppingCart, Cpu, GraduationCap } from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";
import { SECTORS } from "@/data/sectors";
import { AGENT_BY_ID } from "@/data/agents";
import AgentIcon from "@/components/AgentIcon";
import GrowthSimulator from "@/components/GrowthSimulator";
import { buildWhatsAppLink } from "@/lib/contact";

const SECTOR_ICONS = {
  Plane, Briefcase, ShoppingCart, Cpu, GraduationCap,
};

export default function SelenaMatrix() {
  const { t, lang, formatPrice } = useI18n();
  const [activeId, setActiveId] = useState(SECTORS[0].id);

  const active = useMemo(() => SECTORS.find((s) => s.id === activeId), [activeId]);
  const pack = useMemo(() => (active?.pack || []).map((id) => AGENT_BY_ID[id]).filter(Boolean), [active]);
  const totalPhp = useMemo(() => pack.reduce((sum, a) => sum + (a?.price_php || 0), 0), [pack]);

  return (
    <section data-testid="selena-matrix" id="studio" className="relative max-w-7xl mx-auto px-6 lg:px-10 py-16">
      <header className="max-w-3xl mb-8">
        <div className="flex items-center gap-3 mb-4">
          <span className="dncm-pulse-dot" style={{ background: "var(--dncm-green)", boxShadow: "0 0 12px var(--dncm-green)" }} />
          <span className="font-mono-d text-[11px] uppercase tracking-[0.32em] text-white/65">
            Selena Matrix
          </span>
        </div>
        <h3 className="font-display text-3xl sm:text-4xl lg:text-5xl text-white leading-tight">
          {t("studio_title")}
        </h3>
        <p className="mt-2 text-white/65 max-w-2xl">{t("studio_subtitle")}</p>
      </header>

      <div className="grid lg:grid-cols-[280px_1fr] gap-5">
        {/* Sector picker */}
        <div className="dncm-card rounded-2xl p-4">
          <div className="font-mono-d text-[10px] uppercase tracking-[0.26em] text-white/55 mb-3">
            {t("sector_label")}
          </div>
          <div className="space-y-2">
            {SECTORS.map((s) => {
              const Icon = SECTOR_ICONS[s.icon] || Briefcase;
              const isActive = s.id === activeId;
              return (
                <button
                  key={s.id}
                  data-testid={`sector-${s.id}`}
                  onClick={() => setActiveId(s.id)}
                  className={`w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-lg border transition-colors ${
                    isActive
                      ? "border-[color:var(--dncm-green)]/60 bg-[color:var(--dncm-green-soft)]"
                      : "border-white/10 bg-black/30 hover:border-white/20"
                  }`}
                >
                  <span
                    className="flex items-center justify-center w-8 h-8 rounded-md"
                    style={{
                      background: isActive ? "rgba(222,255,154,0.18)" : "rgba(255,255,255,0.04)",
                      color: isActive ? "var(--dncm-green)" : "rgba(255,255,255,0.7)",
                    }}
                  >
                    <Icon className="w-4 h-4" />
                  </span>
                  <span className="flex-1">
                    <span className="block text-white text-sm">{lang === "en" ? s.nameEn : s.name}</span>
                  </span>
                  {isActive && <ArrowRight className="w-3.5 h-3.5 text-[color:var(--dncm-green)]" />}
                </button>
              );
            })}
          </div>
        </div>

        {/* Pack preview */}
        <div className="dncm-card rounded-2xl p-5 sm:p-6" data-testid="selena-pack">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="font-mono-d text-[10px] uppercase tracking-[0.26em] text-white/55">
                Pack recommandé
              </div>
              <div className="mt-1 font-display text-2xl text-white">
                {lang === "en" ? active?.nameEn : active?.name}
              </div>
              <div className="text-white/60 text-sm max-w-xl mt-1">
                {lang === "en" ? active?.pitchEn : active?.pitch}
              </div>
            </div>
            <div className="text-right">
              <div className="font-mono-d text-[10px] uppercase tracking-[0.22em] text-white/55">
                Total {t("activation_label")}
              </div>
              <div className="font-display text-2xl text-[color:var(--dncm-green)] dncm-green-glow">
                {formatPrice(totalPhp)}
              </div>
            </div>
          </div>

          <div className="mt-5 grid sm:grid-cols-2 gap-3">
            {pack.map((a) => (
              <div
                key={a.id}
                className="flex items-center gap-3 rounded-lg border border-white/10 bg-black/40 px-3 py-2.5"
              >
                <span
                  className="flex items-center justify-center w-8 h-8 rounded-md shrink-0"
                  style={{
                    background: `${a.color}1A`,
                    border: `1px solid ${a.color}55`,
                    color: a.color,
                  }}
                >
                  <AgentIcon name={a.icon} className="w-4 h-4" />
                </span>
                <div className="min-w-0">
                  <div className="text-white text-sm truncate">
                    {a.name} <span className="text-white/40">·</span>{" "}
                    <span className="text-white/65 text-xs">{a.role}</span>
                  </div>
                  <div className="font-mono-d text-[10px] uppercase tracking-[0.22em] text-white/45">
                    {formatPrice(a.price_php)}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <a
              data-testid="selena-whatsapp"
              href={buildWhatsAppLink(
                `Bonjour, je veux activer le pack « ${active?.name} » : ${(pack || [])
                  .map((p) => p.name)
                  .join(", ")}.`
              )}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-3 px-6 py-3 rounded-full font-mono-d text-[11px] uppercase tracking-[0.22em] dncm-offer-btn"
            >
              {t("activate_system")} ({formatPrice(totalPhp)})
            </a>
          </div>
        </div>
      </div>

      <div className="mt-8">
        <GrowthSimulator sector={active} />
      </div>
    </section>
  );
}
